import type { ContentPage } from '../content/content-pages';

export const LEGAL_BASE_PATH = '/en/legal/';
export const LEGAL_LAST_UPDATED = '2026-07-18';

export interface LegalPage extends ContentPage {
  legalNotice?: string;
}

export function legalUrl(slug: string): string {
  return `${LEGAL_BASE_PATH}${slug}/`;
}

export const LEGAL_PAGES: LegalPage[] = [
  {
    slug: 'privacy-policy',
    title: 'Privacy Policy (GDPR)',
    description:
      'How IQLabTest collects, uses, shares, protects, and retains personal data, and how to exercise GDPR rights.',
    summary:
      'This policy explains the personal data processed by IQLabTest, the purposes and legal grounds, retention criteria, recipients, and your rights.',
    legalNotice:
      'Pre-launch requirement: replace this notice with the legal name, registered address, registration number, and jurisdiction of the data controller. The repository does not currently contain those details.',
    category: 'Legal',
    sections: [
      {
        heading: 'Controller and contact',
        paragraphs: [
          'The service is provided under the IQLabTest brand at iqlabtest.com. The controller’s full legal name, registered address, registration number, and applicable supervisory authority must be inserted here before commercial launch. Privacy requests can be sent to privacy@iqlabtest.com and general support requests to support@iqlabtest.com.',
          'When an organization invites a candidate, that organization may be an independent controller for its recruitment purpose while IQLabTest processes data under its instructions. The organization must provide its own notice and lawful basis.',
        ],
      },
      {
        heading: 'Data we process',
        paragraphs: [
          'Depending on the service used, we process account and contact data, first and last name, date of birth, language, assessment responses, timing and progress, calculated estimates and confidence ranges, certificate and verification identifiers, consent records, organization membership, support and privacy requests, security logs, and transaction records.',
          'Payment details are handled through the configured hosted payment provider, currently Mollie when enabled. IQLabTest should receive payment status and identifiers rather than complete card or bank credentials.',
          'We use self-hosted, cookieless Umami analytics to understand page use and the assessment funnel. Events can include language, test stage, question position and domain, navigation direction, whether an answer was selected or changed, dwell time, answered count, completion or abandonment, API status and latency, and short-lived random flow and request trace identifiers. Analytics does not receive candidate names, dates of birth, email addresses, answer values, question text, result or certificate identifiers, exact scores, correctness, comments, IP addresses as event data, advertising identifiers, device fingerprints, or cross-site cookies. Optional experience ratings remain linked to the completed test inside the product database and may include a comment the candidate chooses to provide.',
        ],
      },
      {
        heading: 'Purposes and legal grounds',
        paragraphs: [
          'We use data only for specified purposes and apply the legal ground appropriate to each activity.',
        ],
        bullets: [
          'Contract: deliver assessments, reports, certificates, account functions, and purchases.',
          'Consent: record candidate disclosures or optional processing where consent is genuinely required and can be withdrawn.',
          'Legitimate interests: secure the service, prevent abuse, keep proportionate audit records, resolve disputes, and improve reliability after balancing user rights.',
          'Legal obligation: retain or disclose records required by tax, accounting, consumer, or regulatory law.',
        ],
      },
      {
        heading: 'Sharing and international transfers',
        paragraphs: [
          'Data may be shared with hosting, email, monitoring, payment, professional-adviser, and support providers only as needed and under appropriate contracts. Candidate result summaries may be shared with the inviting organization as disclosed before the assessment; individual answers are not intended to be shared.',
          'The production operator must maintain an up-to-date processor list and document any transfer outside the EEA, including the relevant adequacy decision or safeguards such as Standard Contractual Clauses.',
        ],
      },
      {
        heading: 'Your rights',
        paragraphs: [
          'Subject to GDPR conditions, you may request access, rectification, erasure, restriction, portability, or object to processing. You may withdraw consent without affecting earlier lawful processing and request human review of a disputed result. Contact privacy@iqlabtest.com; identity verification may be required.',
          'You may complain to the supervisory authority in your habitual residence, workplace, or the place of an alleged infringement. The controller’s lead authority must be inserted before launch.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Is the IQ result used for automated hiring?',
        answer:
          'The product states that an indicative estimate must not be the sole hiring criterion. Employers must apply human review and other relevant evidence.',
      },
      {
        question: 'How can I request deletion?',
        answer:
          'Use the privacy controls in an authenticated account or email privacy@iqlabtest.com. Some financial, security, or legal-hold records may need to be retained or pseudonymized.',
      },
    ],
    related: ['data-retention-policy', 'cookie-policy', 'certificate-disclaimer'],
    sources: [
      {
        label: 'European Commission — information for individuals under the GDPR',
        url: 'https://commission.europa.eu/law/law-topic/data-protection/information-individuals_en',
      },
      {
        label: 'European Commission — GDPR principles',
        url: 'https://commission.europa.eu/law/law-topic/data-protection/information-business-and-organisations/principles-gdpr_en',
      },
    ],
  },
  {
    slug: 'terms-and-conditions',
    title: 'Terms & Conditions',
    description:
      'Terms governing access to IQLabTest assessments, reports, certificates, accounts, purchases, and organizational use.',
    summary:
      'These terms set the rules for using IQLabTest and explain the status and limitations of assessment results.',
    legalNotice:
      'Pre-launch requirement: insert the contracting legal entity, registered address, registration number, governing law, competent courts, and business contact details.',
    category: 'Legal',
    sections: [
      {
        heading: 'Service and acceptance',
        paragraphs: [
          'By creating an account, starting a paid order, accepting a candidate invitation, or otherwise using IQLabTest, you agree to these terms and the applicable privacy notice. If you act for an organization, you confirm that you are authorized to bind it.',
          'You must provide accurate information, protect credentials and invitation links, and use the service only lawfully. The service is not intended for emergency, medical, or diagnostic use.',
        ],
      },
      {
        heading: 'Assessment results',
        paragraphs: [
          'Results are indicative estimates based on the assessment version, questions presented, answers, timing, and scoring model. They include uncertainty and are not a clinical diagnosis, official standardized IQ credential, educational qualification, or guarantee of future performance.',
          'Organizations must not use a score as the sole basis for hiring, dismissal, education, healthcare, credit, insurance, or another decision with significant effects. Appropriate human review and independent evidence are required.',
        ],
      },
      {
        heading: 'Accounts, availability, and acceptable use',
        paragraphs: [
          'You may not copy secure question banks, automate answers, interfere with scoring, probe security, impersonate another person, misuse certificates, or infringe intellectual-property or privacy rights. We may suspend access reasonably necessary to protect users or the service.',
          'We aim for reliable availability but do not promise uninterrupted operation. Maintenance, security incidents, provider outages, and force majeure may cause interruptions.',
        ],
      },
      {
        heading: 'Purchases and digital delivery',
        paragraphs: [
          'Prices, currency, taxes, product scope, and payment method must be shown before order placement. Paid digital content is unlocked only after server-confirmed payment. Statutory consumer rights remain unaffected.',
          'Where you expressly request immediate digital delivery and acknowledge losing the withdrawal right when performance begins, the statutory withdrawal exception may apply. See the Digital Content & EU Withdrawal Rights Notice and Refund Policy.',
        ],
      },
      {
        heading: 'Liability and changes',
        paragraphs: [
          'Nothing excludes liability that cannot legally be excluded, including mandatory consumer rights. Subject to mandatory law, liability should be limited in the final lawyer-reviewed terms to foreseeable direct loss and an appropriate cap.',
          'Material changes will be dated and communicated where required. The final production terms must identify governing law and dispute channels without depriving consumers of mandatory protections in their country of residence.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Can an employer rely only on my score?',
        answer:
          'No. The service requires contextual interpretation, other job-relevant evidence, and meaningful human review.',
      },
      {
        question: 'Are statutory consumer rights excluded?',
        answer: 'No. Mandatory consumer protections continue to apply.',
      },
    ],
    related: ['refund-policy', 'digital-content-eu-withdrawal-rights', 'disclaimer'],
  },
  {
    slug: 'cookie-policy',
    title: 'Cookie Policy',
    description:
      'The cookies and browser storage used by IQLabTest, their purposes, duration, and available controls.',
    summary:
      'IQLabTest uses necessary session and preference storage plus self-hosted cookieless analytics that does not write an analytics identifier to browser storage. Optional storage-based analytics or marketing must not run without valid consent.',
    category: 'Legal',
    sections: [
      {
        heading: 'What we currently use',
        paragraphs: [
          'The application uses a first-party language preference in localStorage under iqlabtest-language. Authenticated areas use an HTTP-only, secure, same-site session cookie. Temporary technical storage may also support security, load balancing, forms, payments, and assessment continuity.',
          'Necessary storage is used only to provide a service you requested or to maintain security. Blocking it may prevent login, checkout, or saved assessment progress.',
          'Self-hosted Umami records page views and limited product events without setting analytics cookies or writing an analytics identifier to localStorage or sessionStorage. It respects the browser Do Not Track setting and excludes URL query strings and fragments. Session replay is not enabled.',
        ],
      },
      {
        heading: 'Optional cookies',
        paragraphs: [
          'Analytics, advertising, social-media tracking, or other non-essential technologies must remain disabled until you make a specific, informed choice. No such category should be described as necessary merely because it is commercially useful.',
          'If optional tools are added, this policy and the consent interface must list provider, purpose, data, duration, and transfer information before activation.',
        ],
      },
      {
        heading: 'Your controls',
        paragraphs: [
          'You can remove the language preference or clear cookies through browser settings. A consent interface must make rejecting optional cookies as easy as accepting them and allow later withdrawal.',
          'Withdrawing optional consent does not affect the lawfulness of earlier processing, and a minimum website service remains available.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Do necessary cookies require consent?',
        answer:
          'Strictly necessary storage may be exempt from consent, but it must still be explained and limited to the necessary purpose.',
      },
      {
        question: 'Does IQLabTest use advertising cookies?',
        answer:
          'The current application configuration reviewed for this notice does not identify advertising cookies. They must not be introduced without updating this notice and obtaining consent.',
      },
    ],
    related: ['privacy-policy', 'data-retention-policy', 'terms-and-conditions'],
    sources: [
      {
        label: 'Your Europe — online privacy and cookies',
        url: 'https://europa.eu/youreurope/business/dealing-with-customers/data-protection/online-privacy/index_en.htm',
      },
    ],
  },
  {
    slug: 'refund-policy',
    title: 'Refund Policy',
    description:
      'When IQLabTest purchases may be refunded and how digital-content withdrawal and statutory remedies apply.',
    summary:
      'Refund eligibility depends on whether digital delivery has begun, the consent captured, product use, and mandatory consumer law.',
    category: 'Legal',
    sections: [
      {
        heading: 'Consumer purchases',
        paragraphs: [
          'Before digital delivery begins, contact support@iqlabtest.com promptly with the order identifier. If you have not requested immediate performance, the normal statutory withdrawal period may apply.',
          'If you expressly consented to immediate delivery and acknowledged that the withdrawal right would be lost when delivery began, withdrawal may no longer be available after the report or digital content is unlocked. This does not remove remedies for content that is faulty, not supplied, or not as described.',
        ],
      },
      {
        heading: 'Duplicate, failed, and unauthorized payments',
        paragraphs: [
          'Verified duplicate charges and payments for orders that were not delivered will be refunded to the original payment method after reasonable verification. Report suspected unauthorized use immediately and contact the payment provider or bank where appropriate.',
          'Payment-provider processing times, bank delays, exchange-rate effects, and fees outside our control may affect when a refund appears.',
        ],
      },
      {
        heading: 'Organization credits',
        paragraphs: [
          'A full organization-credit refund is available only while enough purchased credits remain unreserved and unconsumed. Reserved or consumed credits cannot be treated as unused. Approved refunds create an auditable reversal rather than deleting the original transaction.',
          'Nothing in this policy limits mandatory rights. Contact support@iqlabtest.com with the account email, order number, reason, and relevant evidence.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Can I get a refund after downloading a report?',
        answer:
          'Not normally where valid express consent and loss-of-withdrawal acknowledgement were captured before immediate delivery, but statutory remedies remain for non-conforming or undelivered content.',
      },
      {
        question: 'How are approved refunds paid?',
        answer: 'They are returned through the original payment method where possible.',
      },
    ],
    related: ['digital-content-eu-withdrawal-rights', 'checkout-consent', 'terms-and-conditions'],
  },
  {
    slug: 'disclaimer',
    title: 'Website and Assessment Disclaimer',
    description:
      'Important limitations for IQLabTest content, IQ-style estimates, educational material, and decision-making.',
    summary:
      'IQLabTest offers educational content and indicative cognitive estimates. It does not provide medical, psychological, legal, or employment advice.',
    category: 'Legal',
    sections: [
      {
        heading: 'No clinical or professional diagnosis',
        paragraphs: [
          'An IQLabTest result is not a clinical diagnosis, neuropsychological evaluation, official intelligence credential, or substitute for a qualified professional. Online conditions, language, health, device, familiarity, and motivation can affect performance.',
          'Educational articles summarize general information and may become outdated. They do not create a professional-client relationship.',
        ],
      },
      {
        heading: 'No guaranteed outcomes',
        paragraphs: [
          'Scores do not guarantee school, career, financial, health, or relationship outcomes. Intelligence is broader than a single test, and people with the same composite estimate can have different strengths and circumstances.',
          'Historical or celebrity IQ pages distinguish documented facts from unsupported estimates. A repeated number is not evidence of an authenticated test.',
        ],
      },
      {
        heading: 'High-stakes decisions',
        paragraphs: [
          'Do not use IQLabTest as the sole basis for employment, education, healthcare, legal status, insurance, credit, or another consequential decision. Use appropriately validated tools, reasonable accommodations, qualified interpretation, and meaningful human review.',
          'External links are provided for context; IQLabTest does not control or endorse all third-party content.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Is this an official IQ test?',
        answer:
          'No. It is an indicative IQ-style assessment and must not be represented as a clinical or standardized credential.',
      },
      {
        question: 'Can I use a certificate for employment?',
        answer:
          'It can verify completion and the recorded indicative estimate, but it is not proof of job fitness or a professional qualification.',
      },
    ],
    related: ['certificate-disclaimer', 'terms-and-conditions', 'privacy-policy'],
  },
  {
    slug: 'digital-content-eu-withdrawal-rights',
    title: 'Digital Content & EU Withdrawal Rights Notice',
    description:
      'How the EU withdrawal period applies to immediately delivered digital reports and the consent needed before delivery begins.',
    summary:
      'EU consumers generally have a withdrawal period for distance contracts, but a digital-content exception can apply after valid express consent, acknowledgement, confirmation, and commencement of delivery.',
    category: 'Legal',
    sections: [
      {
        heading: 'Your general right',
        paragraphs: [
          'EU consumers generally have 14 days to withdraw from many distance contracts without giving a reason. The precise start date and national implementation depend on the contract and applicable law.',
          'Before purchase, the trader must clearly provide its identity, product, total price, payment and performance arrangements, complaint handling, and withdrawal information.',
        ],
      },
      {
        heading: 'Immediate digital delivery',
        paragraphs: [
          'For digital content not supplied on a tangible medium, the withdrawal exception can apply when performance begins after the consumer gives prior express consent to begin during the withdrawal period and acknowledges that this causes the withdrawal right to be lost.',
          'The consent must be an active, unambiguous action—not a pre-ticked box or acceptance hidden inside general terms. Contract confirmation must record the consent and acknowledgement.',
        ],
      },
      {
        heading: 'If the requirements are not met',
        paragraphs: [
          'If valid prior consent, acknowledgement, or required confirmation was not provided, the trader cannot simply rely on the digital-content exception. Statutory rights and remedies remain.',
          'To withdraw before delivery or raise a concern, email support@iqlabtest.com with the order details and a clear statement. This notice does not reduce mandatory national rights.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Does clicking “Pay” automatically waive withdrawal rights?',
        answer:
          'No. Immediate digital performance and loss of withdrawal require a clear, prior, express consent and acknowledgement separate from merely placing the order.',
      },
      {
        question: 'What happens after delivery begins?',
        answer:
          'If all legal conditions were met, the withdrawal exception may apply once digital delivery begins. Remedies for defective or missing content remain.',
      },
    ],
    related: ['checkout-consent', 'refund-policy', 'terms-and-conditions'],
    sources: [
      {
        label: 'EUR-Lex — Consumer Rights Directive 2011/83/EU',
        url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32011L0083',
      },
      {
        label: 'Your Europe — returns and withdrawal',
        url: 'https://europa.eu/youreurope/citizens/consumers/shopping/returns/index_en.htm',
      },
    ],
  },
  {
    slug: 'checkout-consent',
    title: 'Checkout Consent Text',
    description:
      'The express consent and acknowledgement shown before immediate delivery of paid IQLabTest digital content.',
    summary:
      'Checkout uses an unticked acknowledgement for immediate digital delivery and loss of the withdrawal right when performance begins.',
    category: 'Legal',
    sections: [
      {
        heading: 'Required checkbox text',
        paragraphs: [
          '“I expressly request immediate delivery of the digital assessment report before the end of the 14-day withdrawal period. I understand that once digital delivery begins, I lose my right to withdraw from this digital-content purchase.”',
          'The box must be unticked by default, separate from acceptance of the Terms & Conditions, and required only where immediate paid digital delivery is requested.',
        ],
      },
      {
        heading: 'Information shown beside consent',
        paragraphs: [
          'Before the order button, checkout must display the exact product, total price including taxes, currency, delivery method, trader identity and contact details, Terms & Conditions, Refund Policy, Privacy Policy, and this withdrawal notice.',
          'The order confirmation should preserve the consent version, timestamp, account or order identifier, wording accepted, and confirmation of digital delivery.',
        ],
      },
      {
        heading: 'What this page does not do',
        paragraphs: [
          'Visiting this page does not itself provide consent. Consent is collected only through the active checkout control connected to a specific order.',
          'The production operator must have the final text reviewed for its establishment and target markets.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Can the checkbox be preselected?',
        answer: 'No. Express consent requires an active choice.',
      },
      {
        question: 'Is accepting the Terms enough?',
        answer:
          'No. The immediate-performance request and loss-of-withdrawal acknowledgement should be clear and specific.',
      },
    ],
    related: ['digital-content-eu-withdrawal-rights', 'refund-policy', 'terms-and-conditions'],
  },
  {
    slug: 'certificate-disclaimer',
    title: 'Certificate Disclaimer',
    description:
      'What an IQLabTest certificate verifies and the important limits on using or presenting it.',
    summary:
      'A certificate verifies completion and the recorded indicative estimate. It is not an official IQ credential, diagnosis, qualification, or guarantee.',
    category: 'Legal',
    sections: [
      {
        heading: 'What the certificate confirms',
        paragraphs: [
          'The certificate records the candidate name supplied, completion date, verification identifier, assessment language, and indicative result produced for that attempt. The QR or verification URL checks the corresponding server record.',
          'Verification reduces alteration risk but does not independently prove identity, test conditions, supervision, or that another person did not assist.',
        ],
      },
      {
        heading: 'What it does not confirm',
        paragraphs: [
          'The certificate is not a clinical diagnosis, standardized IQ credential, educational award, professional license, membership decision, or proof of suitability for employment.',
          'The result includes measurement uncertainty and may differ across instruments, dates, languages, devices, health conditions, and testing environments.',
        ],
      },
      {
        heading: 'Permitted presentation',
        paragraphs: [
          'Do not alter the certificate, remove its disclaimer, imply accreditation, or present it as issued by a clinical or educational authority. Third parties should use the official verification URL and read this disclaimer.',
          'Organizations must not treat the certificate as the sole basis for a consequential decision.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Does a valid QR code make the score official?',
        answer:
          'No. It verifies the IQLabTest record, not clinical standardization, identity supervision, or professional accreditation.',
      },
      {
        question: 'Can the result be compared with a clinical assessment?',
        answer:
          'Not directly. Different instruments, norms, administration conditions, and purposes can produce different results.',
      },
    ],
    related: ['disclaimer', 'privacy-policy', 'terms-and-conditions'],
  },
  {
    slug: 'data-retention-policy',
    title: 'Data Retention Policy',
    description:
      'How long IQLabTest keeps assessment, account, payment, consent, security, and certificate information.',
    summary:
      'IQLabTest applies storage limitation: fixed periods where configured and documented necessity criteria for financial, security, dispute, and legal-hold records.',
    category: 'Legal',
    sections: [
      {
        heading: 'Retention schedule and criteria',
        paragraphs: [
          'The following reflects the current product design; the production operator must configure and verify deletion jobs before launch.',
        ],
        bullets: [
          'Candidate assessment records: 365 days where the disclosed candidate-consent version applies, unless the inviting organization lawfully configures a shorter period or a documented legal hold applies.',
          'Public and consumer results and certificates: while needed to deliver verification, account history, disputes, or the contract; reviewed at least annually and deleted or anonymized when no longer necessary.',
          'Incomplete sessions and expired invitations: until operational cleanup confirms expiry and releases reserved resources.',
          'Payment, invoice, refund, and tax records: for the period required by applicable accounting, tax, anti-fraud, and legal-claims law.',
          'Consent, security, support, and audit records: only as long as needed to demonstrate compliance, protect the service, resolve disputes, or meet law.',
          'Cookieless analytics events: for the documented operational analytics period, after which they must be deleted or aggregated. Self-hosted Umami retains data until the operator deletes it, so the production retention procedure must be configured and verified before launch.',
        ],
      },
      {
        heading: 'Deletion, anonymization, and legal holds',
        paragraphs: [
          'A deletion request removes linked external identities and direct identifiers where possible. Referentially necessary assessment or financial records may be pseudonymized instead of erased when retention has a valid legal basis.',
          'A documented legal hold can pause deletion for a specific dispute or obligation. Holds require restricted authorization, a reason, audit recording, review, and release when no longer necessary.',
        ],
      },
      {
        heading: 'Backups and processors',
        paragraphs: [
          'Deleted data may remain temporarily in protected backups until normal rotation, without being restored except for disaster recovery. Any restored data must be re-subjected to deletion controls.',
          'Processors must delete or return data at the end of service subject to lawful retention and contract terms. Contact privacy@iqlabtest.com for an access, deletion, or retention question.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Is every record deleted immediately on request?',
        answer:
          'Not always. Data may be retained where required for transactions, security, legal claims, or a valid legal hold; direct identifiers should be removed or pseudonymized where possible.',
      },
      {
        question: 'How long is candidate data kept?',
        answer:
          'The current candidate notice specifies 365 days for the disclosed consent version, subject to a shorter lawful configuration or a documented legal hold.',
      },
    ],
    related: ['privacy-policy', 'cookie-policy', 'certificate-disclaimer'],
    sources: [
      {
        label: 'European Commission — GDPR storage limitation principle',
        url: 'https://commission.europa.eu/law/law-topic/data-protection/information-business-and-organisations/principles-gdpr_en',
      },
    ],
  },
];

export const LEGAL_PAGE_MAP = new Map(LEGAL_PAGES.map((page) => [page.slug, page]));
